import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BenefitType, Box, GuideDot } from 'edenred-ui';

import { FormFields } from '@epi-forms/helpers';
import {
  FundsLoadingTypes,
  Payment,
  Schedule
} from '@epi-constants/benefitTypes';
import { getNumber } from '@epi-helpers/numeral';
import { getBenefitSelected } from '@epi-selectors/benefitGroupSettings';
import { IUpdateFooter } from '@epi-models/pages/TransferPage';
import { isTomorrow } from '@epi-helpers/TopupHelper';
import { isEmptyObj } from '@epi-helpers/isEmptyObj';
import { useOnboardingPayment, useOnboardingWizard } from '@epi-shared/hooks';
import {
  getOnboardingIsSecondStep,
  getOnboardingLoadBenefitPageType
} from '@epi-store/onboarding/onboarding.selectors';
import {
  MIN_ALLOWED_BALANCE,
  TopupFormStatusesType
} from '@epi-constants/topupForm';
import { getCurrentLunchFundsLoadingType } from '@epi-selectors/settings';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';

import {
  useTopupEmployees,
  useTopupPendingBenefits,
  useTopupRequest,
  useTopupSwitch
} from '../../../hooks';

import { TopupInProgress } from './TopupInProgress';
import { TopupConfirmationModal } from './TopupConfirmationModal';
import { MyChoiceInfo } from './MyChoiceInfo';
import { BlockedDirectLoadInfo } from './BlockedDirectLoadInfo';
import {
  BenefitSelection,
  LoadAmount,
  PaymentMethodStep,
  ScheduleLoad,
  LoadEmployeesForm,
  TopupFormNotifications
} from './TopupFormSteps';
import { getTopupInProgressRequest } from '@epi-api/topupLoad';
import { showNotification } from '@epi-actions/overlays';

export const TopupForm = ({
  updateFooter,
  form,
  loadableBenefits
}: IUpdateFooter) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showTopupForm, setShowTopupForm] = useState(true);
  const [anotherTopupCheckPending, setAnotherTopupCheckPending] =
    useState<boolean>(false);
  const [topupRequestStatus, setTopupRequestStatus] =
    useState<TopupFormStatusesType | null>(null);

  const language = useSelector(getApplicationLanguageId);

  const {
    watch,
    handleSubmit,
    setValue,
    resetField,
    getValues,
    reset,
    formState: { isValid, errors }
  } = form;

  const benefitType = watch(FormFields.Benefit);
  const benefitGroupIds = watch(FormFields.BenefitGroupIds);
  const topupPerPerson = watch(FormFields.MoneyAmount);
  const scheduleType = watch(FormFields.ScheduleType);
  const paymentMethod = watch(FormFields.PaymentMethod);
  const eInvoiceAddress = watch(FormFields.EInvoiceAddress);
  const operator = watch(FormFields.EInvoiceOperator);
  const daysToTopup = watch(FormFields.DaysForLunari);
  const benefitSettings = useSelector(state =>
    getBenefitSelected(state, benefitType)
  );

  const currentLunchFundsLoadingType = useSelector(
    getCurrentLunchFundsLoadingType
  );

  const {
    paginationCount,
    toggleEmployee,
    checkedCount,
    lowestYearlyLimit,
    topupBeneficiariesIds,
    toggleAllEmployee,
    isAllEmployeeChecked,
    setForceUpdate,
    formattedTopup,
    formattedFee,
    formattedVat,
    formattedTopupWithFees,
    formattedRemainingBalance,
    remainingBalance,
    fee,
    vat,
    formattedMissingAmount,
    daysExceed,
    employeesToTopup,
    checkableEmployees
  } = useTopupEmployees();

  const { handleToggler, isToggle, showOnHoldBtn } = useTopupSwitch();

  const { isOnBoardingActive, flags, refetchFlags } = useOnboardingWizard();
  const onboardingPayment = useOnboardingPayment(reset);
  const isSecondStep = useSelector(getOnboardingIsSecondStep);
  const onboardingPageType = useSelector(getOnboardingLoadBenefitPageType);

  const onboardingBackButton = isOnBoardingActive
    ? {
        backText: t('components.onboarding.buttons.back'),
        onBack: onboardingPayment?.onboardingLoadBenefit?.onBack
          ? () => {
              if (onboardingPayment?.onboardingLoadBenefit?.onBack) {
                onboardingPayment?.onboardingLoadBenefit?.onBack();
              }
            }
          : undefined
      }
    : {};

  const onboardingCancelButton = isOnBoardingActive
    ? {
        cancelText: t('components.onboarding.buttons.skip'),
        onCancel: onboardingPayment?.onboardingLoadBenefit?.onSkip
          ? () => {
              if (onboardingPayment?.onboardingLoadBenefit?.onSkip) {
                onboardingPayment?.onboardingLoadBenefit.onSkip();
              }
            }
          : undefined,
        disableCancel:
          isSecondStep && !flags?.isMoneyTransfered && !flags?.isLoadInitiated
      }
    : {};

  const { postTopupRequest, isTopupPending, isTopupProcessing, summaryId } =
    useTopupRequest(
      setShowConfirmationModal,
      isToggle,
      showOnHoldBtn,
      setTopupRequestStatus
    );

  const { isPendingBenefit, getPendingBenefits } = useTopupPendingBenefits(
    topupBeneficiariesIds
  );

  const isBatchOrder = checkedCount > 50 && scheduleType === Schedule.Direct;

  const isPaymentTypeTopUpRequest = checkableEmployees.length
    ? checkableEmployees.some(
        ({ fundsLoadingType }) =>
          fundsLoadingType === FundsLoadingTypes.TopUpRequest
      )
    : currentLunchFundsLoadingType === FundsLoadingTypes.TopUpRequest;

  const isPaymentTypeDirectLoad = checkableEmployees.some(
    ({ fundsLoadingType }) => fundsLoadingType === FundsLoadingTypes.DirectLoad
  );

  const isLunchTopupRequest =
    isPaymentTypeTopUpRequest && benefitType === BenefitType.lunch;

  const isLunchDirectLoad =
    isPaymentTypeDirectLoad && benefitType === BenefitType.lunch;

  const onSubmit = async () => {
    setAnotherTopupCheckPending(true);
    if (scheduleType === Schedule.SelectDate) {
      const benefit = getValues(FormFields.Benefit);
      getPendingBenefits(benefit);
    }
    const isTopupInProgress = await getTopupInProgressRequest();
    if (isTopupInProgress.isTopUpLoadInProgress) {
      dispatch(
        showNotification(
          { message: `messages.another_top_up_load_in_progress` },
          'error'
        )
      );
    } else {
      setShowConfirmationModal(true);
    }
    setAnotherTopupCheckPending(false);
  };
  const handleCloseModal = () => setShowConfirmationModal(false);

  const handleTopupRequest = () => {
    const payload = {
      amount: topupPerPerson ? (getNumber(topupPerPerson) * 100).toFixed(0) : 0,
      numberOfWorkingDays: isLunchDirectLoad
        ? Number(getValues(FormFields.DaysForLunari))
        : 0,
      benefitType,
      beneficiariesIds: topupBeneficiariesIds,
      paymentMethod,
      eInvoiceAddress,
      operator
    };
    postTopupRequest(payload);
    resetField(FormFields.MoneyAmount);
    resetField(FormFields.Benefit);
    resetField(FormFields.BenefitGroupIds);
  };

  const handleResetTopupStatus = () => {
    setTopupRequestStatus(null);
  };

  useEffect(() => {
    if (loadableBenefits?.length === 1) {
      setValue(FormFields.Benefit, loadableBenefits[0]);
    }
  }, [summaryId, showTopupForm, isSecondStep]);

  useEffect(() => {
    if ((isTopupProcessing || isTopupPending) && !isOnBoardingActive) {
      setShowTopupForm(false);
    }
  }, [isTopupProcessing, isTopupPending]);

  useEffect(() => {
    refetchFlags();
  }, [onboardingPageType]);

  useEffect(() => {
    if (showTopupForm) {
      updateFooter({
        submitText: t('containers.deposit_money.topup_tab.footer_button'),
        onSubmit: handleSubmit(onSubmit),
        hidden: isLunchTopupRequest,
        disableSubmit:
          anotherTopupCheckPending ||
          !isValid ||
          !isEmptyObj(errors) ||
          (remainingBalance < MIN_ALLOWED_BALANCE &&
            paymentMethod === Payment.Deduction) ||
          !paymentMethod ||
          !topupBeneficiariesIds.length ||
          !checkedCount ||
          (scheduleType === Schedule.SelectDate &&
            !isTomorrow(watch(FormFields.ScheduleDate))) ||
          (benefitType === BenefitType.lunch &&
            (!daysToTopup || daysToTopup === 0)),
        ...onboardingBackButton,
        ...onboardingCancelButton
      });
    }
    if (!showTopupForm) {
      updateFooter(undefined);
    }
  }, [
    showTopupForm,
    isValid,
    remainingBalance,
    topupBeneficiariesIds.length,
    isEmptyObj(errors),
    paymentMethod,
    scheduleType,
    benefitType,
    employeesToTopup,
    language,
    daysToTopup,
    onboardingPageType,
    isSecondStep,
    isLunchTopupRequest,
    benefitGroupIds,
    anotherTopupCheckPending,
    flags?.isMoneyTransfered,
    flags?.isLoadInitiated
  ]);

  if (!showTopupForm && !isOnBoardingActive) {
    return (
      <TopupInProgress
        setShowTopupForm={setShowTopupForm}
        isTopupProcessing={isTopupProcessing || isTopupPending}
        summaryId={summaryId}
        loadableBenefits={loadableBenefits}
        setForceUpdate={setForceUpdate}
      />
    );
  }

  return (
    <Box>
      <BlockedDirectLoadInfo blockDirectLoad={isLunchTopupRequest} />
      <MyChoiceInfo loadableBenefits={loadableBenefits} />
      <Box position="relative">
        {isOnBoardingActive && (
          <>
            {loadableBenefits.length > 1 && (
              <Box position="absolute" top={80} left={595}>
                <GuideDot
                  storageKey="load-benefit-selection-guide-dot"
                  tooltipDescription={t('guided_tour.benefit_selection')}
                />
              </Box>
            )}
            <Box position="absolute" top={335} left={595}>
              <GuideDot
                storageKey="load-schedule-guide-dot"
                tooltipDescription={t('guided_tour.schedule_load')}
              />
            </Box>
          </>
        )}
        <BenefitSelection
          loadableBenefits={loadableBenefits}
          onboardingPageType={onboardingPageType}
        />
        <ScheduleLoad isLunchTopupRequest={isLunchTopupRequest} />

        <LoadAmount
          daysExceed={daysExceed}
          daysToTopup={daysToTopup}
          remainingBalance={remainingBalance}
          benefitType={benefitType}
          lowestYearlyLimit={lowestYearlyLimit}
          benefitSettings={benefitSettings}
        />
        <Box position="relative">
          {isOnBoardingActive && !!paginationCount && (
            <Box position="absolute" top={120} left={445}>
              <GuideDot
                storageKey="load-beneficiaries-guide-dot"
                tooltipDescription={t('guided_tour.load_beneficiaries')}
              />
            </Box>
          )}
          <LoadEmployeesForm
            isLunchTopupRequest={isLunchTopupRequest}
            formattedTopup={formattedTopup}
            formattedVat={formattedVat}
            paginationCount={paginationCount}
            toggleEmployee={toggleEmployee}
            toggleAllEmployee={toggleAllEmployee}
            checkedCount={checkedCount}
            isAllEmployeeChecked={isAllEmployeeChecked}
            formattedFee={formattedFee}
            formattedTopupWithFees={formattedTopupWithFees}
            formattedRemainingBalance={formattedRemainingBalance}
            remainingBalance={remainingBalance}
            fee={fee}
            vat={vat}
            handleToggler={handleToggler}
            isToggle={isToggle}
            showOnHoldBtn={showOnHoldBtn}
          />
        </Box>
        <Box position="relative">
          <PaymentMethodStep
            isLunchTopupRequest={isLunchTopupRequest}
            remainingBalance={remainingBalance}
            formattedMissingAmount={formattedMissingAmount}
          />
          {isOnBoardingActive && (
            <Box position="absolute" top={70} left={270}>
              <GuideDot
                storageKey="load-payment-method-guide-dot"
                tooltipDescription={t('guided_tour.payment_method')}
              />
            </Box>
          )}
        </Box>
        <TopupFormNotifications
          isLunchTopupRequest={isLunchTopupRequest}
          benefitType={benefitType}
        />
        <TopupConfirmationModal
          showModal={showConfirmationModal}
          employeesCount={checkedCount}
          handleCloseModal={handleCloseModal}
          handleTopupRequest={handleTopupRequest}
          totalAmount={formattedTopupWithFees}
          isTopupPending={isTopupPending}
          showOnHoldText={isToggle}
          topupAmount={formattedTopup}
          isPendingBenefit={
            isPendingBenefit && scheduleType === Schedule.SelectDate
          }
          showOnboardingContent={
            (isOnBoardingActive || isTopupPending || !!topupRequestStatus) &&
            (!showTopupForm || isTopupPending || !!topupRequestStatus)
          }
          topupRequestStatus={topupRequestStatus}
          resetTopupStatus={handleResetTopupStatus}
          isBatchOrder={isBatchOrder}
        />
      </Box>
    </Box>
  );
};

TopupForm.displayName = 'TopupForm';
